import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2, Clock, AlertTriangle, AlertOctagon, Eye, TrafficCone, FileDown } from "lucide-react";
import api, { money } from "../lib/api";
import { SearchableSelect } from "../components/SearchableSelect";
import { filterRows } from "../lib/search";
import ListFilters from "../components/ListFilters";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const LIGHTS = [
  { key: "al_dia", label: "Al día", dot: "bg-green-500", ring: "ring-green-500", text: "text-green-500", bg: "bg-green-500/10", border: "border-green-500/20", Icon: CheckCircle2 },
  { key: "por_vencer", label: "Por vencer", dot: "bg-amber-500", ring: "ring-amber-500", text: "text-amber-500", bg: "bg-amber-500/10", border: "border-amber-500/20", Icon: Clock },
  { key: "vencida", label: "Vencida", dot: "bg-orange-500", ring: "ring-orange-500", text: "text-orange-500", bg: "bg-orange-500/10", border: "border-orange-500/20", Icon: AlertTriangle },
  { key: "critica", label: "Crítica", dot: "bg-red-500", ring: "ring-red-500", text: "text-red-500", bg: "bg-red-500/10", border: "border-red-500/20", Icon: AlertOctagon },
];
const LMAP = Object.fromEntries(LIGHTS.map((l) => [l.key, l]));
const amtStr = (amt) => Object.entries(amt).map(([c, v]) => money(c, v)).join(" · ") || money("GTQ", 0);

export default function Semaforo() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [vendor, setVendor] = useState("all");
  const [client, setClient] = useState("all");
  const [color, setColor] = useState("all");

  useEffect(() => { api.get("/invoices/semaforo").then((r) => setRows(r.data)).catch(() => {}).finally(() => setLoading(false)); }, []);

  const vendors = useMemo(() => [...new Set(rows.map((r) => r.vendor_name).filter(Boolean))].sort(), [rows]);
  const clients = useMemo(() => {
    const m = new Map();
    rows.forEach((r) => { if (r.client_id) m.set(r.client_id, r.client_name); });
    return [...m.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);

  const base = useMemo(() => filterRows(rows, { q, fields: ["number", "client_name", "vendor_name"], equals: [{ field: "vendor_name", value: vendor }, { field: "client_id", value: client }] }), [rows, q, vendor, client]);

  const summary = useMemo(() => {
    const s = {}; LIGHTS.forEach((l) => (s[l.key] = { count: 0, amt: {} }));
    base.forEach((r) => { const b = s[r.light]; if (!b) return; b.count++; b.amt[r.currency] = (b.amt[r.currency] || 0) + r.balance; });
    return s;
  }, [base]);

  const shown = color === "all" ? base : base.filter((r) => r.light === color);

  const exportCSV = () => {
    const headers = ["Semaforo", "Factura", "Cliente", "Vendedor", "Emision", "Vence", "Dias mora", "Dias para vencer", "Estado", "Moneda", "Saldo"];
    const lines = shown.map((r) => [LMAP[r.light]?.label || r.light, r.number, r.client_name, r.vendor_name, r.date, r.due_date, r.days_overdue, r.days_to_due, r.status, r.currency, r.balance]
      .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","));
    const csv = [headers.join(","), ...lines].join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `semaforo-financiero-${new Date(Date.now()-216e5).toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="semaforo-cards">
        {LIGHTS.map((l) => {
          const s = summary[l.key]; const active = color === l.key;
          return (
            <button key={l.key} onClick={() => setColor(active ? "all" : l.key)} data-testid={`semaforo-card-${l.key}`}
              className={`text-left rounded-xl border ${l.border} ${l.bg} p-4 transition-all ${active ? `ring-2 ${l.ring}` : "hover:brightness-95"}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className={`h-3 w-3 rounded-full ${l.dot}`} />
                <l.Icon className={`h-4 w-4 ${l.text}`} />
                <span className={`text-xs font-bold uppercase tracking-widest ${l.text}`}>{l.label}</span>
              </div>
              <div className={`text-3xl font-bold ${l.text}`}>{s.count}</div>
              <div className="text-xs text-slate-600 font-mono mt-1">{amtStr(s.amt)}</div>
            </button>
          );
        })}
      </div>

      <ListFilters searchTestId="semaforo-search" search={q} onSearch={setQ} placeholder="Buscar factura, cliente..."
        vendors={vendors} vendor={vendor} onVendor={setVendor}
        extra={
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Cliente</label>
            <div className="w-52">
            <SearchableSelect value={client} onChange={setClient} testid="semaforo-client"
              placeholder="Todos" searchPlaceholder="Escribe para buscar cliente..."
              options={[{ value: "all", label: "Todos" }, ...clients.map((c) => ({ value: c.id, label: c.name }))]} />
            </div>
          </div>
        }
        onClear={() => { setQ(""); setVendor("all"); setClient("all"); setColor("all"); }}
        right={<Button variant="outline" className="rounded-sm gap-2 text-xs h-9" onClick={exportCSV} data-testid="semaforo-export"><FileDown className="h-4 w-4" /> Exportar CSV</Button>} />

      <Card className="rounded-sm border shadow-none overflow-hidden">
        {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div> :
          shown.length === 0 ? <div className="flex flex-col items-center py-16 text-muted-foreground"><TrafficCone className="h-8 w-8 mb-2" /><p className="text-sm">No hay facturas con saldo pendiente para este filtro.</p></div> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="semaforo-table">
                <thead><tr className="bg-muted text-left text-xs uppercase tracking-widest text-muted-foreground">
                  <th className="px-4 py-3 font-bold">Semáforo</th><th className="px-4 py-3 font-bold">Factura</th><th className="px-4 py-3 font-bold">Cliente</th>
                  <th className="px-4 py-3 font-bold">Vendedor</th><th className="px-4 py-3 font-bold">Vence</th><th className="px-4 py-3 font-bold">Estado</th>
                  <th className="px-4 py-3 font-bold text-right">Saldo</th><th className="px-4 py-3 font-bold text-right">Acción</th></tr></thead>
                <tbody>
                  {shown.map((r) => { const l = LMAP[r.light]; return (
                    <tr key={r.id} className="border-t border-border hover:bg-muted/50" data-testid={`semaforo-row-${r.number}`}>
                      <td className="px-4 py-2.5"><span className="inline-flex items-center gap-2"><span className={`h-2.5 w-2.5 rounded-full ${l.dot}`} /><span className={`text-xs font-semibold ${l.text}`}>{l.label}</span></span></td>
                      <td className="px-4 py-2.5 font-mono text-xs font-bold text-primary">{r.number}</td>
                      <td className="px-4 py-2.5 font-medium">{r.client_name}</td>
                      <td className="px-4 py-2.5 text-xs">{r.vendor_name || "—"}</td>
                      <td className="px-4 py-2.5 text-xs">{r.due_date}
                        {r.days_overdue > 0 ? <span className="text-red-600 font-semibold"> · {r.days_overdue}d mora</span> : <span className="text-muted-foreground"> · vence en {r.days_to_due}d</span>}</td>
                      <td className="px-4 py-2.5 text-xs">{r.status}</td>
                      <td className="px-4 py-2.5 text-right font-mono font-bold">{money(r.currency, r.balance)}</td>
                      <td className="px-4 py-2.5 text-right"><Button variant="ghost" size="icon" className="h-8 w-8" title={r.doc_type === "delivery" ? "Ver envío" : "Ver factura"} onClick={() => navigate(r.doc_type === "delivery" ? `/envios?open=${r.id}` : `/facturas?q=${encodeURIComponent(r.number)}&open=${r.id}`)} data-testid={`semaforo-view-${r.number}`}><Eye className="h-4 w-4" /></Button></td>
                    </tr>
                  ); })}
                </tbody>
              </table>
            </div>
          )}
      </Card>
    </div>
  );
}
